"use client";

import { useTranslations } from "next-intl";
import React, { useState, useEffect, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { SellerCheckoutNoteBanner } from "./seller-checkout-note-banner";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ShippingForm, type ShippingFormData } from "./shipping-form";
import { OrderSummary } from "./order-summary";
import { CountdownTimer } from "./countdown-timer";
import { CHECKOUT_COUNTDOWN_SECONDS, calculateServiceFee } from "@/lib/config";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { PreorderCheckoutBlock, type PreorderItemSummary } from "./preorder-checkout-block";
import { isStateRequiredForCountry } from "@kraahl/contract";

export type CheckoutFormProps = {
  subtotal: number;
  shippingTotal: number;
  serviceFee: number;
  total: number;
  currencyCode?: string;
  sellerCheckoutNote?: string | null;
  // FIX-10: optional so auction checkout can disable form-submit (Enter key) path; use PayPal button instead
  onCompleteAction?: (formData: ShippingFormData, saveAddress?: boolean) => void;
  profile?: { id?: string; email?: string; phone?: string } | null;
  userId?: string | null;
  /**
   * Render prop for the payment rail. Third argument is a context with
   * callbacks the rail can use to push live data BACK INTO the form so the
   * "Payment $X.XX" header and OrderSummary refresh in real time:
   *
   *  - `onLiveRateAmountChange(amount | null)` — Shippo buyer-rate preview;
   *    `null` means "no live rate active" (legacy flat-rate or buyer cancelled
   *    the subflow). Wired by `PayPalCheckout`'s `onLiveRateAmountChange`.
   *  - `effectiveTotal` — the same number the Payment header + OrderSummary
   *    display (= `total` when no live rate active, otherwise
   *    `subtotal + liveRateShipping + recomputed serviceFee`). PayPalCheckout
   *    callers MUST forward this as `<PayPalCheckout totalAmount={…}>` so
   *    Apple Pay / Google Pay native sheets display the same amount the
   *    buyer just confirmed in the form header. Passing the upstream base
   *    `total` lets the wallet sheet show a stale number — the buyer's
   *    Confirm tap is on a discrepant amount vs the server-resolved charge.
   *
   * Every PayPalCheckout caller MUST forward both fields so the override
   * is caller-agnostic — the logic lives here (single SoT) rather than being
   * duplicated in payment-sheet / cart / chat.
   *
   * (A third field, `darkUi`, was deleted in Phase 2 step 2f: the overlay
   * panel follows the site theme now, so the Stripe iframe reads
   * `resolvedTheme` itself — see `stripe-embedded-checkout-inline.tsx`.)
   */
  renderPaymentAction?: (
    formData: ShippingFormData | null,
    selectedCountry: string,
    ctx: {
      onLiveRateAmountChange: (amount: number | null) => void;
      effectiveTotal: number;
    },
  ) => React.ReactNode;
  className?: string;
  embedded?: boolean;
  hideShippingAddressTitle?: boolean;
  showCountdown?: boolean;
  onCountdownExpireAction?: () => void;
  preorderItems?: PreorderItemSummary[];
  onPreorderConsentChangeAction?: (consented: boolean) => void;
  inDarkSheetBody?: boolean;
};

/**
 * Address-only completeness — the 7 fields any downstream rate-shopping
 * adapter needs to ship a quote request. Intentionally LOOSER than
 * `isShippingFormComplete` (no email/phone): the live-rate sub-flow needs
 * `formData` early so the rate picker can mount and auto-fetch quotes
 * while the buyer is still typing contact info. The PayPal CTA gate stays
 * tied to the strict variant via the `disabled` prop on `PayPalCheckout`.
 *
 * Order/keys mirror `formDataToRateAddress` in `paypal-checkout.tsx` so a
 * grep across the two stays trivial. `state` is included for every country —
 * the buyer-side builder is a pure normaliser (no NA whitelist), and the
 * server is the authoritative destination gate: if Shippo returns 0 rates
 * the rate picker surfaces `NO_RATES_TO_DESTINATION` per PLAN §A "Checkout
 * fallback" rather than failing silently.
 */
export function isShippingAddressComplete(formData: ShippingFormData | null) {
  if (!formData) return false;
  const { firstName, lastName, address, city, state, postalCode, country } = formData;
  // `state` is required only for countries that actually have provinces in
  // PayPal/Shippo terms (US, CA). Address Book stores `state = ""` for every
  // other country, and that's the buyer-truthful value — the gate must accept
  // it. Drift between this gate and `ShippingForm`'s internal validator was
  // the 2026-06-25 silent-empty-checkout class. SoT: `isStateRequiredForCountry`.
  const stateOk = isStateRequiredForCountry(country) ? !!state?.trim() : true;
  return (
    !!firstName?.trim() &&
    !!lastName?.trim() &&
    !!address?.trim() &&
    !!city?.trim() &&
    stateOk &&
    !!postalCode?.trim() &&
    !!country?.trim()
  );
}

// Strict variant — address + contact (email/phone). Gates the PayPal CTA
// and the legacy submit button. Exported so cart/chat/payment-sheet can
// reproduce the disable rule on their own `disabled` prop (the looser
// `renderPaymentAction` pass-through no longer enforces it).
export function isShippingFormComplete(formData: ShippingFormData | null) {
  if (!isShippingAddressComplete(formData)) return false;
  const { email, phoneCountryCode, phoneNumber } = formData!;
  return (
    !!email?.trim() &&
    !!phoneCountryCode?.trim() &&
    !!phoneNumber?.trim()
  );
}

/**
 * Missing-field enumeration for the LIVE checkout form (vs the saved-address
 * variant in shipping-form.tsx#getMissingSavedAddressFields, which checks a
 * SavedAddress row from address_book). Returns the same key-set the form
 * labels are translated by, so callers can render
 *   `Please fill in {firstName, email, phone}`
 * without duplicating the missing-detection logic. Empty array = form is
 * strict-complete (== `isShippingFormComplete` would return true).
 *
 * Exists because every "Pay" button (PayPal SDK / Stripe / legacy submit)
 * disables silently when this is non-empty — no per-field error UI, no
 * tooltip, no browser-native `required` popover (the buttons live OUTSIDE
 * the `<form>` so submit-time validation never fires). Without surfacing
 * the list the buyer has no signal *which* field is keeping the button grey.
 */
export function getMissingShippingFormFields(
  formData: ShippingFormData | null,
): string[] {
  if (!formData) return ["firstName", "lastName", "address", "city", "postalCode", "country", "email", "phone"];
  const missing: string[] = [];
  if (!formData.firstName?.trim()) missing.push("firstName");
  if (!formData.lastName?.trim()) missing.push("lastName");
  if (!formData.address?.trim()) missing.push("address");
  if (!formData.city?.trim()) missing.push("city");
  if (!formData.postalCode?.trim()) missing.push("postalCode");
  if (!formData.country?.trim()) missing.push("country");
  if (isStateRequiredForCountry(formData.country) && !formData.state?.trim()) missing.push("state");
  if (!formData.email?.trim()) missing.push("email");
  if (!formData.phoneCountryCode?.trim() || !formData.phoneNumber?.trim() || formData.phoneNumber.trim().length < 4) missing.push("phone");
  return missing;
}

export function CheckoutForm({
  subtotal,
  shippingTotal,
  serviceFee,
  total,
  currencyCode = DEFAULT_CURRENCY,
  sellerCheckoutNote = null,
  onCompleteAction,
  profile,
  userId,
  renderPaymentAction,
  className,
  embedded = false,
  hideShippingAddressTitle = false,
  showCountdown = false,
  onCountdownExpireAction,
  preorderItems = [],
  onPreorderConsentChangeAction,
  inDarkSheetBody = false,
}: CheckoutFormProps) {
  const t = useTranslations("checkout");
  const tFields = useTranslations("shippingForm");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState<ShippingFormData | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [saveAddress, setSaveAddress] = useState(false);
  const [preorderConsent, setPreorderConsent] = useState(false);
  const hasPreorder = preorderItems.length > 0;

  // Live-rate buyer preview. PayPalCheckout (via renderPaymentAction ctx)
  // notifies us whenever the buyer highlights a shipping rate; we override
  // the displayed shipping / service-fee / total so the Payment header and
  // OrderSummary refresh in real time. `null` ⇒ no live rate active (legacy
  // flat-rate or buyer cancelled) ⇒ fall back to props verbatim. The override
  // lives here (single SoT) so every caller — payment-sheet, cart drawer,
  // chat dialog — gets the behaviour without duplicating the math.
  const [liveRateAmount, setLiveRateAmount] = useState<number | null>(null);
  const { effectiveShippingTotal, effectiveServiceFee, effectiveTotal } =
    useMemo(() => {
      const shipping = liveRateAmount ?? shippingTotal;
      const fee =
        liveRateAmount === null
          ? serviceFee
          : subtotal === 0
            ? 0
            : calculateServiceFee(subtotal + liveRateAmount);
      const tot =
        liveRateAmount === null ? total : subtotal + shipping + fee;
      return {
        effectiveShippingTotal: shipping,
        effectiveServiceFee: fee,
        effectiveTotal: tot,
      };
    }, [liveRateAmount, subtotal, shippingTotal, serviceFee, total]);

  useEffect(() => {
    if (!hasPreorder && preorderConsent) setPreorderConsent(false);
  }, [hasPreorder, preorderConsent]);

  useEffect(() => {
    onPreorderConsentChangeAction?.(!hasPreorder || preorderConsent);
  }, [hasPreorder, preorderConsent, onPreorderConsentChangeAction]);

  const handleFormDataChange = (isValid: boolean, data: ShippingFormData, shouldSave?: boolean) => {
    setIsFormValid(isValid);
    setFormData(data);
    setSaveAddress(!!shouldSave);
  };

  // Surface which required fields keep the Pay buttons disabled. Pay buttons
  // (PayPal SDK / Stripe / legacy submit) live OUTSIDE the `<form>`, so the
  // HTML5 `required` attribute on inputs never produces a browser tooltip —
  // the button just stays grey with no signal *why*. We render the list as
  // a non-blocking warning above the payment block when the buyer has
  // started filling the form but at least one required field is still empty.
  const missingFields = useMemo(() => getMissingShippingFormFields(formData), [formData]);
  const hasUserStartedInput = useMemo(() => {
    if (!formData) return false;
    return Boolean(
      formData.firstName?.trim() ||
        formData.lastName?.trim() ||
        formData.address?.trim() ||
        formData.city?.trim() ||
        formData.postalCode?.trim() ||
        formData.country?.trim() ||
        formData.state?.trim() ||
        formData.email?.trim() ||
        formData.phoneNumber?.trim(),
    );
  }, [formData]);
  // Localise field keys. Note `address` → `streetAddress` — the input's i18n
  // label key in `shippingForm`, not the JSON object key on the form data.
  const fieldLabelKey = (key: string): string =>
    key === "address" ? "streetAddress" : key;
  const missingFieldLabels = useMemo(
    () => missingFields.map((k) => tFields(fieldLabelKey(k) as Parameters<typeof tFields>[0])),
    [missingFields, tFields],
  );
  const showMissingFieldsWarning = hasUserStartedInput && missingFields.length > 0;

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onCompleteAction) return;
    if (!isShippingFormComplete(formData) || !isFormValid) return;
    if (hasPreorder && !preorderConsent) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      onCompleteAction?.(formData!, saveAddress);
    }, 1500);
  };

  const paymentBlock = (
    // `min-w-0` is load-bearing: as a grid item of `.grid grid-cols-1 gap-6`
    // below, its default `min-width: auto` (= min-content) would inflate the
    // column to the widest descendant's min-content — in live-rate mode, the
    // rate cards' nowrap "Carrier · Service" + price would push the whole
    // sheet body past the viewport (operator-reported overflow 2026-06-25).
    <div className="min-w-0">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-lg text-foreground">{t("payment")}</h3>
        <span className="font-semibold text-lg text-foreground">{formatPrice(effectiveTotal, currencyCode)}</span>
      </div>
      {showMissingFieldsWarning && (
        <div
          className="mb-4 flex items-start gap-3 rounded-3xl border px-4 py-3 border-amber-400/50 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p className="text-sm leading-snug">
            {t("missingFieldsWarning", { fields: missingFieldLabels.join(", ") })}
          </p>
        </div>
      )}
      {renderPaymentAction ? (
        <div className={cn("min-w-0", hasPreorder && !preorderConsent ? "pointer-events-none opacity-50" : undefined)}>
          {/* Looser gate: pass `formData` once the *address* is complete
              (incl. state) so the live-rate sub-flow can mount and start
              fetching quotes. Strict completeness (email/phone + preorder
              consent) is reapplied on the PayPal CTA via the consumer's
              `disabled` prop — see cart.tsx / payment-sheet.tsx / chat. */}
          {renderPaymentAction(
            isShippingAddressComplete(formData) ? formData : null,
            selectedCountry,
            {
              onLiveRateAmountChange: setLiveRateAmount,
              effectiveTotal,
            },
          )}
        </div>
      ) : (
        <Button
          type="submit"
          className="w-full h-12 bg-[#0070ba] hover:bg-[#005ea6] text-white rounded-full"
          onClick={handleSubmit}
          disabled={!isShippingFormComplete(formData) || isSubmitting || (hasPreorder && !preorderConsent)}
        >
          {isSubmitting ? t("processing") : t("payWithPayPal")}
        </Button>
      )}
    </div>
  );

  const form = (
    <form
      onSubmit={handleSubmit}
      className={cn(
        inDarkSheetBody ? "px-4 pt-4" : "p-4 pt-4",
        inDarkSheetBody ? "pb-4" : "pb-8",
      )}
    >
      {embedded && showCountdown && onCountdownExpireAction ? (
        <div className="rounded-2xl overflow-hidden mb-4 border border-sheet-handle">
          <CountdownTimer
            initialSeconds={CHECKOUT_COUNTDOWN_SECONDS}
            onExpire={onCountdownExpireAction}
            className="border-sheet-handle"
          />
        </div>
      ) : null}
      {/* grid-cols-1 + [&>*]:min-w-0 — both load-bearing for mobile sheet overflow:
          - grid-cols-1 expands to `repeat(1, minmax(0, 1fr))`, pinning the
            implicit column so a wide descendant (live-rate cards) can't
            inflate it past the viewport
          - [&>*]:min-w-0 cancels grid-items' default min-width:auto, so an
            unbreakable child (long policy URL) can't push the grid past the
            sheet width and get clipped by overflow-x-hidden */}
      <div className="grid grid-cols-1 gap-6 [&>*]:min-w-0">
        {sellerCheckoutNote?.trim() ? (
          <SellerCheckoutNoteBanner note={sellerCheckoutNote} />
        ) : null}
        <div>
          {!hideShippingAddressTitle && (
            <h3 className="font-semibold text-lg mb-4 text-foreground">{t("shippingAddress")}</h3>
          )}
          <ShippingForm
            onFormDataChangeAction={handleFormDataChange}
            onCountryChangeAction={handleCountryChange}
            profile={profile}
            userId={userId || profile?.id}
            deferKeyboardFocus={inDarkSheetBody}
          />
        </div>

        <Separator className="bg-sheet-handle" />

        <div>
          <h3 className="font-semibold text-lg mb-4 text-foreground">{t("orderSummary")}</h3>
          <OrderSummary
            subtotal={subtotal}
            shippingTotal={effectiveShippingTotal}
            serviceFee={effectiveServiceFee}
            currencyCode={currencyCode}
            showTotal={false}
          />
        </div>

        <Separator className="bg-sheet-handle" />

        {hasPreorder && (
          <>
            <PreorderCheckoutBlock
              items={preorderItems}
              consented={preorderConsent}
              onConsentChange={setPreorderConsent}
            />
            <Separator className="bg-sheet-handle" />
          </>
        )}

        {paymentBlock}
      </div>
    </form>
  )

  if (embedded) {
    return (
      <div className={cn("w-full min-h-0", className)}>
        {form}
      </div>
    )
  }

  return (
    <div className={cn("h-full flex flex-col overflow-x-hidden", className ?? "bg-card text-foreground")}>
      <div className="flex-1 overflow-y-auto overflow-x-hidden">{form}</div>
    </div>
  )
}
